<template>
    <div class="confirmDeleteModal">
        <!-- Pergunta Principal -->
        <div class="confirmDeleteModal_header">
            <div class="confirmDeleteModal_question">
                <h3>Tem certeza que deseja excluir o link de compartilhamento</h3>
                <div class="confirmDeleteModal_itemName">
                    "{{ itemName }}"?
                </div>
            </div>
        </div>

        <!-- Detalhes do Item -->
        <div v-if="details" class="confirmDeleteModal_details">
            <div class="confirmDeleteModal_info">
                <div v-if="details.shareUrl" class="confirmDeleteModal_linkSection">
                    <div class="confirmDeleteModal_linkHeader">
                        <sw-icon name="link" />
                        <label>URL que será desativada:</label>
                    </div>
                    <div class="confirmDeleteModal_linkContainer">
                        <div class="confirmDeleteModal_link">
                            {{ details.shareUrl }}
                        </div>
                        <sw-tooltip content="Visualizar link">
                            <sw-button
                                icon="earth"
                                circle-icon
                                size="sm"
                                hover-color="blue"
                                @click="openLink(details.shareUrl)"
                            />
                        </sw-tooltip>
                    </div>
                </div>

                <div v-if="details.description" class="confirmDeleteModal_description">
                    <sw-icon name="info" />
                    <span>{{ details.description }}</span>
                </div>

                <div v-if="details.stats" class="confirmDeleteModal_stats">
                    <div class="confirmDeleteModal_stat">
                        <sw-icon name="eye" />
                        <span>{{ details.stats.views || 0 }} visualizações</span>
                    </div>
                    <div class="confirmDeleteModal_stat">
                        <sw-icon name="calendar" />
                        <span>{{ formatDate(details.createdAt) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Aviso Importante -->
        <div class="confirmDeleteModal_warning">
            <AlertMessage
                :variant="ALERT_VARIANTS.INFO"
                title="O que acontecerá"
                :description="DELETE_MODAL_MESSAGES.CONFIRM_WARNING"
            />
        </div>

        <!-- Ações -->
        <div class="confirmDeleteModal_actions">
            <sw-button
                :name="DELETE_MODAL_MESSAGES.BUTTON_DELETE"
                :color="BUTTON_COLORS.RED"
                :loading="loading"
                @click="handleConfirm"
            />
            <sw-button
                :name="DELETE_MODAL_MESSAGES.BUTTON_CANCEL"
                :color="BUTTON_COLORS.GRAY"
                @click="handleCancel"
            />
        </div>
    </div>
</template>

<script setup>
    import {
        DELETE_MODAL_MESSAGES,
        BUTTON_COLORS,
        ALERT_VARIANTS
    } from '@/constants';
    import AlertMessage from '@/components/alerts/AlertMessage.vue';

    const props = defineProps({
        itemName: {
            type: String,
            required: true
        },
        details: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['confirm', 'cancel']);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(new Date(dateString));
    };

    const handleConfirm = () => {
        emit('confirm');
    };

    const handleCancel = () => {
        emit('cancel');
    };

    const openLink = (url) => {
        window.open(url, '_blank');
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    .confirmDeleteModal {
        display: flex;
        flex-direction: column;
        gap: 2em;
    }

    /* Header da Pergunta */
    .confirmDeleteModal_header {
        text-align: center;
        padding-bottom: 1.5em;
        border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
    }

    .confirmDeleteModal_question {
        h3 {
            font-size: 1.25em;
            font-weight: 600;
            color: var(--textPrimary);
            margin-bottom: 0.75em;
            line-height: 1.3;
        }
    }

    .confirmDeleteModal_itemName {
        font-size: 1.125em;
        font-weight: 700;
        color: var(--textPrimary);
        margin-top: 0.75em;
        display: inline-block;
    }

    /* Detalhes do Item */
    .confirmDeleteModal_details {
        background: var(--backgroundInsideBox, #f8fafc);
        border: 1px solid var(--borderColorLight, #e5e7eb);
        border-radius: 0.875em;
        padding: 1.25em;
    }

    .confirmDeleteModal_info {
        display: flex;
        flex-direction: column;
        gap: 1.25em;
    }

    .confirmDeleteModal_linkSection {
        margin-bottom: 0.5em;
    }

    .confirmDeleteModal_linkHeader {
        display: flex;
        align-items: center;
        gap: 0.5em;
        margin-bottom: 0.5em;

        .sw-icon {
            color: var(--colorRed, #ef4444);
            font-size: 1em;
        }

        label {
            font-size: 0.875em;
            font-weight: 600;
            color: var(--textSecondary);
            margin: 0;
        }
    }

    .confirmDeleteModal_linkContainer {
        display: flex;
        gap: 0.75em;
        align-items: center;
    }

    .confirmDeleteModal_link {
        flex: 1;
        min-width: 0;
        font-family: monospace;
        font-size: 0.875em;
        color: var(--textPrimary);
        background: white;
        padding: 0.75em;
        border-radius: 0.5em;
        border: 1px solid var(--borderColorLight, #e5e7eb);
        word-break: break-all;
        line-height: 1.4;
        overflow-wrap: break-word;
    }

    .confirmDeleteModal_linkContainer .sw-tooltip {
        flex-shrink: 0;
    }

    .confirmDeleteModal_description {
        display: flex;
        align-items: center;
        gap: 0.5em;
        font-size: 0.9375em;
        color: var(--textSecondary);
        line-height: 1.5;
        padding: 1em;
        background: white;
        border-radius: 0.5em;
        border: 1px solid var(--borderColorLight, #e5e7eb);

        .sw-icon {
            color: var(--colorBlue, #3b82f6);
            font-size: 1em;
            flex-shrink: 0;
        }
    }

    .confirmDeleteModal_stats {
        display: flex;
        gap: 1.5em;
        flex-wrap: wrap;
    }

    .confirmDeleteModal_stat {
        display: flex;
        align-items: center;
        gap: 0.5em;
        font-size: 0.875em;
        color: var(--textSecondary);

        .sw-icon {
            font-size: 1em;
            color: var(--colorBlue, #3b82f6);
        }
    }

    /* Seção de Aviso */
    .confirmDeleteModal_warning {
        margin: 0;
    }

    .confirmDeleteModal_actions {
        display: flex;
        gap: 1em;
        justify-content: flex-end;
        padding-top: 1em;
        border-top: 1px solid var(--borderColorLight, #e5e7eb);

        .sw-button {
            min-width: 6em;
        }
    }

    /* Responsividade - Mobile */
    @include screen_medias(s) {
        .confirmDeleteModal {
            gap: 1.5em;
        }

        .confirmDeleteModal_header {
            padding-bottom: 1em;
        }

        .confirmDeleteModal_question {
            h3 {
                font-size: 1.125em;
            }
        }

        .confirmDeleteModal_itemName {
            font-size: 1em;
            margin-top: 0.5em;
        }

        .confirmDeleteModal_details {
            padding: 1em;
        }

        .confirmDeleteModal_info {
            gap: 1em;
        }

        .confirmDeleteModal_description {
            padding: 0.875em;
        }

        .confirmDeleteModal_linkContainer {
            flex-direction: column;
            gap: 0.5em;
        }

        .confirmDeleteModal_link {
            font-size: 0.8125em;
            padding: 0.625em;
        }

        .confirmDeleteModal_stats {
            flex-direction: column;
            gap: 0.75em;
        }

        .confirmDeleteModal_stat {
            font-size: 0.8125em;
        }

        .confirmDeleteModal_actions {
            flex-direction: column;
            gap: 0.75em;

            .sw-button {
                width: 100%;
                min-width: auto;
            }
        }
    }
</style>